import { supabase } from './supabase';

// ─── PROFILE ────────────────────────────────────

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

export async function updateProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ─── MODULE COMPLETIONS ─────────────────────────

export async function saveModuleCompletion(userId, moduleKey, submoduleKey, data, options = {}) {
  const row = {
    user_id: userId,
    module_key: moduleKey,
    submodule_key: submoduleKey,
    status: 'complete',
    data: data,
    completed_at: new Date().toISOString(),
  };
  if (options.isEntryTeaser) row.is_entry_teaser = true;

  const { data: result, error } = await supabase
    .from('module_completions')
    .upsert(row, { onConflict: 'user_id,module_key,submodule_key' })
    .select()
    .single();
  if (error) throw error;
  return result;
}

export async function getModuleCompletions(userId) {
  const { data, error } = await supabase
    .from('module_completions')
    .select('*')
    .eq('user_id', userId)
    .order('completed_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function getModuleCompletion(userId, moduleKey, submoduleKey) {
  let query = supabase
    .from('module_completions')
    .select('*')
    .eq('user_id', userId)
    .eq('module_key', moduleKey);

  if (submoduleKey) {
    query = query.eq('submodule_key', submoduleKey);
  } else {
    query = query.is('submodule_key', null);
  }

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data;
}

// ─── USER TAGS ──────────────────────────────────

export async function saveTags(userId, tags) {
  // tags = [{ tag_key, tag_value, tag_type, source_module, confidence }]
  const rows = tags.map(t => ({
    user_id: userId,
    tag_key: t.tag_key,
    tag_value: t.tag_value,
    tag_type: t.tag_type,
    source_module: t.source_module,
    confidence: t.confidence || 1.0,
  }));

  const { error } = await supabase
    .from('user_tags')
    .upsert(rows, {
      onConflict: 'user_id,tag_key,tag_type',
    });
  if (error) throw error;
}

export async function getUserTags(userId) {
  const { data, error } = await supabase
    .from('user_tags')
    .select('*')
    .eq('user_id', userId);
  if (error) throw error;
  return data;
}

// ─── RELATIONSHIPS PROFILE ──────────────────────

export async function saveRelationshipsProfile(userId, profileData) {
  const { data, error } = await supabase
    .from('relationships_profile')
    .upsert({
      user_id: userId,
      ...profileData,
    }, {
      onConflict: 'user_id',
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getRelationshipsProfile(userId) {
  const { data, error } = await supabase
    .from('relationships_profile')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ─── TRAIT DISPLAY CHOICES (shadow layer) ───────
// Logs every statement/symbol offered during public-profile editing and
// whether it was displayed. Hidden traits are signal. Append-only: each save
// re-logs with a fresh changed_at. Never rendered, never sent to the API.

export async function logTraitDisplayChoices(userId, rows) {
  if (!rows?.length) return;
  const { error } = await supabase
    .from('trait_display_choices')
    .insert(rows.map(r => ({ user_id: userId, ...r })));
  if (error) throw error;
}

// ─── COMPLETED MODULE COUNT ─────────────────────

export async function getCompletedModuleCount(userId) {
  const { data, error } = await supabase
    .from('module_completions')
    .select('module_key')
    .eq('user_id', userId)
    .eq('status', 'complete');
  if (error) throw error;

  const uniqueModules = new Set(data.map(d => d.module_key));
  return uniqueModules.size;
}

// ─── ARCHETYPE HELPER ───────────────────────────────
export async function saveArchetypeToDb(userId, result, answers) {
  if (!result) return;
  try {
    await saveModuleCompletion(userId, "archetype", "cultural_traditions", {
      // The Relationship Archetype is how you show up for people (2026-10).
      archetype: result.offersArchetype,
      offersArchetype: result.offersArchetype,
      needsArchetype: result.needsArchetype,
      connectionSignal: result.connectionSignal,
      isPhilautia: result.isPhilautia,
      needsScores: result.needsScores,
      offersScores: result.offersScores,
      selfScore: result.selfScore,
      greekLoves: result.greekLoves,
      readiness: result.readiness,
      answers: answers || [],
    });
    const primaryKey = result.offersArchetype;
    const tags = [
      { tag_key: "archetype", tag_value: primaryKey, tag_type: "offers", source_module: "archetype" },
    ];
    if (result.greekLoves?.length > 0) {
      tags.push({ tag_key: "top_tradition_1", tag_value: result.greekLoves[0], tag_type: "offers", source_module: "archetype" });
    }
    if (result.greekLoves?.length > 1) {
      tags.push({ tag_key: "top_tradition_2", tag_value: result.greekLoves[1], tag_type: "offers", source_module: "archetype" });
    }
    await saveTags(userId, tags);
  } catch (e) {
    console.error("Failed to save archetype to DB:", e);
  }
}
